import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { supabaseAdmin } from "@/integrations/supabase/client.server";
import { getStripe } from "./stripe.server";
import { PLANS, TRIAL_DAYS, planKeyFromAccountPlan, type PlanKey, type BillingInterval } from "./billing-plans";

const CheckoutInput = z.object({
  plan: z.enum(["starter", "pro", "super"]),
  interval: z.enum(["monthly", "annual"]),
  // Where to send the customer after success/cancel (origin only)
  origin: z.string().url().max(255),
});

const SignupCheckoutInput = z.object({
  email: z.string().trim().email().max(255),
  plan: z.enum(["starter", "pro", "super"]),
  interval: z.enum(["monthly", "annual"]),
  origin: z.string().url().max(255),
});

const CompleteSignupInput = z.object({
  session_id: z.string().min(1).max(255),
  full_name: z.string().trim().min(1).max(120),
  password: z.string().min(8).max(200),
});

const SessionLookupInput = z.object({
  session_id: z.string().min(1).max(255),
});


async function findOrCreateAccountForUser(
  userId: string,
  email: string,
): Promise<{ accountId: string; stripeCustomerId: string | null }> {
  // Find an account this user owns/admins. Default to the first one returned.
  const { data: member } = await supabaseAdmin
    .from("account_members")
    .select("account_id, accounts(id, stripe_customer_id, name)")
    .eq("user_id", userId)
    .order("invited_at", { ascending: true })
    .limit(1)
    .maybeSingle();

  if (member?.account_id) {
    const acc = (member as { accounts: { id: string; stripe_customer_id: string | null } | null }).accounts;
    return {
      accountId: member.account_id,
      stripeCustomerId: acc?.stripe_customer_id ?? null,
    };
  }

  // No account yet, bootstrap a trial account so checkout has somewhere to land.
  const trialEnds = new Date(Date.now() + TRIAL_DAYS * 24 * 60 * 60 * 1000).toISOString();
  const { data: account, error } = await supabaseAdmin
    .from("accounts")
    .insert({
      name: email.split("@")[0] ?? "Workspace",
      plan: "trial",
      plan_status: "trialing",
      trial_ends_at: trialEnds,
    })
    .select("id")
    .single();
  if (error || !account) throw new Error(error?.message ?? "Failed to create account");

  await supabaseAdmin.from("account_members").insert({
    account_id: account.id,
    user_id: userId,
    role: "owner",
  });

  return { accountId: account.id, stripeCustomerId: null };
}

export const createCheckoutSession = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) => CheckoutInput.parse(d))
  .handler(async ({ data, context }) => {
    const { userId, claims } = context;
    const email = (claims.email as string | undefined) ?? "";
    if (!email) throw new Error("User email not available");

    const { accountId, stripeCustomerId } = await findOrCreateAccountForUser(userId, email);

    const stripe = getStripe();
    const planDef = PLANS[data.plan];
    const priceId = planDef.priceIds[data.interval];

    // Reuse customer or create a new one tied to this account
    let customerId = stripeCustomerId;
    if (!customerId) {
      const existing = await stripe.customers.list({ email, limit: 1 });
      if (existing.data.length > 0) {
        customerId = existing.data[0].id;
      } else {
        const created = await stripe.customers.create({
          email,
          metadata: { account_id: accountId, user_id: userId },
        });
        customerId = created.id;
      }
      await supabaseAdmin
        .from("accounts")
        .update({ stripe_customer_id: customerId, updated_at: new Date().toISOString() })
        .eq("id", accountId);
    }

    const session = await stripe.checkout.sessions.create({
      mode: "subscription",
      customer: customerId,
      line_items: [{ price: priceId, quantity: 1 }],
      // Force a card on file even during the free trial.
      payment_method_collection: "always",
      subscription_data: {
        trial_period_days: TRIAL_DAYS,
        trial_settings: {
          end_behavior: { missing_payment_method: "cancel" },
        },
        metadata: { account_id: accountId },
      },
      metadata: { account_id: accountId, plan: data.plan, interval: data.interval },
      allow_promotion_codes: true,
      success_url: `${data.origin}/checkout-return?status=success&session_id={CHECKOUT_SESSION_ID}`,
      cancel_url: `${data.origin}/checkout-return?status=cancel`,
    });

    return { url: session.url };
  });

export const createCustomerPortalSession = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) => z.object({ origin: z.string().url().max(255) }).parse(d))
  .handler(async ({ data, context }) => {
    const { userId } = context;
    const { data: member } = await supabaseAdmin
      .from("account_members")
      .select("account_id, accounts(stripe_customer_id)")
      .eq("user_id", userId)
      .order("invited_at", { ascending: true })
      .limit(1)
      .maybeSingle();

    const customerId = (
      member as { accounts: { stripe_customer_id: string | null } | null } | null
    )?.accounts?.stripe_customer_id;
    if (!customerId) throw new Error("No Stripe customer for this account yet. Start a subscription first.");

    const stripe = getStripe();
    const portal = await stripe.billingPortal.sessions.create({
      customer: customerId,
      return_url: `${data.origin}/settings/billing`,
    });
    return { url: portal.url };
  });

export const getMyBillingAccount = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    const { data: member } = await supabase
      .from("account_members")
      .select("account_id")
      .eq("user_id", userId)
      .order("invited_at", { ascending: true })
      .limit(1)
      .maybeSingle();
    if (!member?.account_id) return { account: null };

    const { data: acc } = await supabase
      .from("accounts")
      .select(
        "id,name,plan,plan_status,trial_ends_at,billing_interval,stripe_customer_id,stripe_subscription_id,stripe_price_id,workspace_limit,team_member_limit,import_history_retention_days,current_period_end,cancel_at_period_end,dunning_state,dunning_started_at,is_admin_comped",
      )
      .eq("id", member.account_id)
      .maybeSingle();

    if (!acc) return { account: null };

    return {
      account: {
        ...acc,
        plan_key: planKeyFromAccountPlan(acc.plan),
      },
    };
  });

// ---------------------------------------------------------------------------
// Signup flow: card + plan FIRST, account created after successful checkout.
// ---------------------------------------------------------------------------

async function findAuthUserByEmail(email: string): Promise<string | null> {
  const normalized = email.trim().toLowerCase();
  const { data, error } = await supabaseAdmin.auth.admin.listUsers({ page: 1, perPage: 200 });
  if (error) return null;
  const match = data.users.find((u) => (u.email ?? "").toLowerCase() === normalized);
  return match?.id ?? null;
}

export const createSignupCheckoutSession = createServerFn({ method: "POST" })
  .inputValidator((d) => SignupCheckoutInput.parse(d))
  .handler(async ({ data }) => {
    const email = data.email.trim().toLowerCase();

    const existingUserId = await findAuthUserByEmail(email);
    if (existingUserId) {
      throw new Error("An account already exists for this email. Please sign in instead.");
    }

    const stripe = getStripe();
    const planDef = PLANS[data.plan];
    const priceId = planDef.priceIds[data.interval];

    let customerId: string;
    const existing = await stripe.customers.list({ email, limit: 1 });
    if (existing.data.length > 0) {
      customerId = existing.data[0].id;
    } else {
      const created = await stripe.customers.create({
        email,
        metadata: { signup_email: email },
      });
      customerId = created.id;
    }

    const session = await stripe.checkout.sessions.create({
      mode: "subscription",
      customer: customerId,
      line_items: [{ price: priceId, quantity: 1 }],
      payment_method_collection: "always",
      subscription_data: {
        trial_period_days: TRIAL_DAYS,
        trial_settings: { end_behavior: { missing_payment_method: "cancel" } },
        metadata: { signup_email: email, plan: data.plan, interval: data.interval },
      },
      metadata: { signup_email: email, plan: data.plan, interval: data.interval, flow: "signup" },
      allow_promotion_codes: true,
      success_url: `${data.origin}/complete-signup?session_id={CHECKOUT_SESSION_ID}`,
      cancel_url: `${data.origin}/signup?cancelled=1`,
    });

    return { url: session.url };
  });

export const getSignupSession = createServerFn({ method: "POST" })
  .inputValidator((d) => SessionLookupInput.parse(d))
  .handler(async ({ data }) => {
    const stripe = getStripe();
    const session = await stripe.checkout.sessions.retrieve(data.session_id);
    if (session.metadata?.flow !== "signup") {
      throw new Error("Invalid signup session.");
    }
    const email = (session.customer_details?.email ?? session.metadata?.signup_email ?? "").toLowerCase();
    if (!email) throw new Error("Signup session has no email yet.");
    const plan = (session.metadata?.plan ?? "pro") as PlanKey;
    const interval = (session.metadata?.interval ?? "monthly") as BillingInterval;
    const planDef = PLANS[plan];

    const existingUserId = await findAuthUserByEmail(email);

    return {
      email,
      plan,
      interval,
      plan_name: planDef.name,
      price: planDef.display[interval],
      status: session.status,
      already_completed: Boolean(existingUserId),
    };
  });

export const completeSignup = createServerFn({ method: "POST" })
  .inputValidator((d) => CompleteSignupInput.parse(d))
  .handler(async ({ data }) => {
    const stripe = getStripe();
    const session = await stripe.checkout.sessions.retrieve(data.session_id, {
      expand: ["subscription"],
    });
    if (session.metadata?.flow !== "signup") {
      throw new Error("Invalid signup session.");
    }
    if (session.status !== "complete") {
      throw new Error("Checkout is not complete yet. Please finish payment first.");
    }
    const email = (session.customer_details?.email ?? session.metadata?.signup_email ?? "").toLowerCase();
    if (!email) throw new Error("Signup session has no email.");

    const customerId = typeof session.customer === "string" ? session.customer : session.customer?.id;
    if (!customerId) throw new Error("Stripe customer missing on session.");

    const subscription = session.subscription as import("stripe").Stripe.Subscription | null;
    if (!subscription) throw new Error("Subscription missing on session.");

    const plan = (session.metadata?.plan ?? "pro") as PlanKey;
    const interval = (session.metadata?.interval ?? "monthly") as BillingInterval;
    const planDef = PLANS[plan];

    const existingUserId = await findAuthUserByEmail(email);
    if (existingUserId) {
      throw new Error("An account already exists for this email. Please sign in instead.");
    }

    const { data: created, error: createErr } = await supabaseAdmin.auth.admin.createUser({
      email,
      password: data.password,
      email_confirm: true,
      user_metadata: { full_name: data.full_name },
    });
    if (createErr || !created.user) {
      throw new Error(createErr?.message ?? "Failed to create user");
    }
    const userId = created.user.id;

    const subItem = subscription.items.data[0];
    const periodEndUnix =
      (subItem as unknown as { current_period_end?: number })?.current_period_end ??
      (subscription as unknown as { current_period_end?: number }).current_period_end ??
      null;
    const trialEndUnix = subscription.trial_end ?? null;

    // The handle_new_user() trigger auto-creates a placeholder account + owner
    // membership for every new auth.users row. Find that and update it in-place
    // instead of creating a duplicate.
    const { data: existingMember } = await supabaseAdmin
      .from("account_members")
      .select("account_id")
      .eq("user_id", userId)
      .order("invited_at", { ascending: true })
      .limit(1)
      .maybeSingle();

    const accountUpdates = {
      name: data.full_name || email.split("@")[0] || "Workspace",
      plan: planDef.accountPlan,
      plan_status: (["active","canceled","past_due","paused","payment_required","trialing"].includes(subscription.status) ? subscription.status : "trialing") as "active"|"canceled"|"past_due"|"paused"|"payment_required"|"trialing",
      billing_interval: interval,
      stripe_customer_id: customerId,
      stripe_subscription_id: subscription.id,
      stripe_price_id: subItem?.price.id ?? planDef.priceIds[interval],
      workspace_limit: planDef.limits.workspace_limit,
      team_member_limit: planDef.limits.team_member_limit,
      import_history_retention_days: planDef.limits.import_history_retention_days,
      trial_ends_at: trialEndUnix ? new Date(trialEndUnix * 1000).toISOString() : null,
      current_period_end: periodEndUnix ? new Date(periodEndUnix * 1000).toISOString() : null,
      cancel_at_period_end: subscription.cancel_at_period_end,
    };

    let accountId: string;
    if (existingMember?.account_id) {
      const { error: updErr } = await supabaseAdmin
        .from("accounts")
        .update(accountUpdates)
        .eq("id", existingMember.account_id);
      if (updErr) {
        await supabaseAdmin.auth.admin.deleteUser(userId);
        throw new Error(updErr.message);
      }
      accountId = existingMember.account_id;
    } else {
      const { data: account, error: accErr } = await supabaseAdmin
        .from("accounts")
        .insert(accountUpdates)
        .select("id")
        .single();
      if (accErr || !account) {
        await supabaseAdmin.auth.admin.deleteUser(userId);
        throw new Error(accErr?.message ?? "Failed to create account");
      }
      const { error: memberErr } = await supabaseAdmin.from("account_members").insert({
        account_id: account.id,
        user_id: userId,
        role: "owner",
      });
      if (memberErr) {
        await supabaseAdmin.from("accounts").delete().eq("id", account.id);
        await supabaseAdmin.auth.admin.deleteUser(userId);
        throw new Error(memberErr.message);
      }
      accountId = account.id;
    }
    const account = { id: accountId };

    try {
      await stripe.subscriptions.update(subscription.id, {
        metadata: { ...(subscription.metadata ?? {}), account_id: account.id },
      });
    } catch {
      // Non-fatal, webhook will still find the account via stripe_customer_id.
    }

    return { ok: true, email, account_id: account.id };
  });
